import React, { useMemo } from "react";
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingDown, Calendar, AlertTriangle, DollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

export default function FluxoCaixaFuturo({ contas, saldoAtual = 0 }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Agrupar contas por semana nos próximos 3 meses
  const fluxoSemanal = useMemo(() => {
    const hoje = new Date();
    const fim = addDays(hoje, 90);
    const semanas = [];
    let dataAtual = hoje;
    let saldoAcumulado = saldoAtual;

    while (dataAtual <= fim) {
      const inicioSemana = dataAtual;
      const fimSemana = addDays(dataAtual, 6);
      
      const contasSemana = contas.filter(c => {
        if (c.status === 'pago') return false;
        const vencimento = parseISO(c.data_vencimento);
        return vencimento >= inicioSemana && vencimento <= fimSemana;
      });

      const totalSemana = contasSemana.reduce((acc, c) => acc + (c.valor || 0), 0);
      saldoAcumulado -= totalSemana;

      semanas.push({
        semana: format(inicioSemana, "dd/MM"),
        label: format(inicioSemana, "dd MMM", { locale: ptBR }),
        total: totalSemana,
        saldo: saldoAcumulado,
        contas: contasSemana.length
      });

      dataAtual = addDays(dataAtual, 7);
    }

    return semanas;
  }, [contas, saldoAtual]);

  // Resumo por categoria
  const resumoCategoria = useMemo(() => {
    const pendentes = contas.filter(c => c.status !== 'pago');
    const categorias = {};
    
    pendentes.forEach(c => {
      const cat = c.categoria || 'outros';
      if (!categorias[cat]) {
        categorias[cat] = { total: 0, qtd: 0 };
      }
      categorias[cat].total += c.valor || 0;
      categorias[cat].qtd += 1;
    });

    return Object.entries(categorias)
      .map(([categoria, dados]) => ({ categoria, ...dados }))
      .sort((a, b) => b.total - a.total);
  }, [contas]);

  // Totais
  const totais = useMemo(() => {
    const pendentes = contas.filter(c => c.status !== 'pago');
    const hoje = new Date();
    
    const proximos7dias = pendentes.filter(c => {
      const venc = parseISO(c.data_vencimento);
      return venc >= hoje && venc <= addDays(hoje, 7);
    }).reduce((acc, c) => acc + (c.valor || 0), 0);

    const proximos30dias = pendentes.filter(c => {
      const venc = parseISO(c.data_vencimento);
      return venc >= hoje && venc <= addDays(hoje, 30);
    }).reduce((acc, c) => acc + (c.valor || 0), 0);

    const proximos90dias = pendentes.reduce((acc, c) => acc + (c.valor || 0), 0);

    const atrasadas = pendentes.filter(c => {
      const venc = parseISO(c.data_vencimento);
      return isBefore(venc, hoje);
    }).reduce((acc, c) => acc + (c.valor || 0), 0);

    return { proximos7dias, proximos30dias, proximos90dias, atrasadas };
  }, [contas]);

  const categoriaLabels = {
    folha_pagamento: "Folha",
    impostos: "Impostos",
    energia: "Energia",
    agua: "Água",
    aluguel: "Aluguel",
    financiamento: "Financ.",
    insumos: "Insumos",
    combustivel: "Combust.",
    manutencao: "Manut.",
    veterinario: "Veter.",
    transporte: "Transp.",
    seguro: "Seguros",
    consultoria: "Consult.",
    outros: "Outros"
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-medium text-stone-800">{label}</p>
          <p className="text-red-600 text-sm">A pagar: {formatCurrency(payload[0]?.value)}</p>
          <p className="text-sm text-stone-500">{payload[0]?.payload?.contas} conta(s)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs text-red-100">Atrasadas</span>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totais.atrasadas)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4" />
              <span className="text-xs text-amber-100">Próx. 7 dias</span>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totais.proximos7dias)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs text-blue-100">Próx. 30 dias</span>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totais.proximos30dias)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs text-purple-100">Próx. 90 dias</span>
            </div>
            <p className="text-xl font-bold">{formatCurrency(totais.proximos90dias)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Fluxo */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-600" />
            Projeção de Pagamentos (Próximos 90 dias)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fluxoSemanal}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 11 }}
                  interval={1}
                />
                <YAxis 
                  tickFormatter={(value) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(value)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Resumo por Categoria */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle>Compromissos por Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          {resumoCategoria.length === 0 ? (
            <p className="text-center text-stone-500 py-4">Nenhuma conta pendente</p>
          ) : (
            <div className="space-y-3">
              {resumoCategoria.map(({ categoria, total, qtd }) => {
                const percentual = totais.proximos90dias > 0 ? (total / totais.proximos90dias) * 100 : 0;
                return (
                  <div key={categoria} className="flex items-center gap-4">
                    <div className="w-24 text-sm font-medium text-stone-600 truncate">
                      {categoriaLabels[categoria] || categoria}
                    </div>
                    <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-400 to-red-500 rounded-full transition-all"
                        style={{ width: `${Math.min(percentual, 100)}%` }}
                      />
                    </div>
                    <div className="text-right min-w-[100px]">
                      <p className="font-semibold text-stone-800">{formatCurrency(total)}</p>
                      <p className="text-xs text-stone-500">{qtd} conta(s)</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}